import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Button } from '../ui/button';
import { LayoutDashboard, Car, Wallet, History, LogOut, BarChart3, Star, Power, DollarSign } from 'lucide-react';
import { toast } from '../../hooks/use-toast';

const DriverLayout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isOnline, setIsOnline] = useState(false);
  const [toggling, setToggling] = useState(false);

  useEffect(() => {
    fetchOnlineStatus();
  }, []);

  const fetchOnlineStatus = async () => {
    try {
      const api = (await import('../../utils/api')).default;
      const response = await api.get('/drivers/my-profile');
      setIsOnline(response.data.driver?.isOnline || false);
    } catch (error) {
      console.error('Error fetching online status:', error);
    }
  };

  const toggleOnlineStatus = async () => {
    setToggling(true);
    try {
      const api = (await import('../../utils/api')).default;
      const newStatus = !isOnline;
      await api.put('/drivers/status', { isOnline: newStatus });
      setIsOnline(newStatus);
      toast({
        title: newStatus ? 'You are now Online' : 'You are now Offline',
        description: newStatus 
          ? 'You can now receive ride requests' 
          : 'You will not receive new ride requests',
      });
    } catch (error) {
      console.error('Error toggling status:', error);
      toast({
        title: 'Error',
        description: 'Failed to update status',
        variant: 'destructive',
      });
    } finally {
      setToggling(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('tekstar_token');
    localStorage.removeItem('tekstar_user');
    navigate('/login');
  };

  const menuItems = [
    { path: '/driver/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { path: '/driver/rides', icon: Car, label: 'Available Rides' },
    { path: '/driver/history', icon: History, label: 'My Rides' },
    { path: '/driver/wallet', icon: Wallet, label: 'Wallet' },
    { path: '/driver/earnings', icon: DollarSign, label: 'Earnings' },
    { path: '/driver/analytics', icon: BarChart3, label: 'Analytics' },
    { path: '/driver/reviews', icon: Star, label: 'Reviews' },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top Navigation */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-pink-500 to-purple-600 rounded-lg flex items-center justify-center">
                <Car className="h-6 w-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">Tekstar Driver</h1>
                <p className="text-xs text-gray-500">Driver Portal</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {/* Online Status Toggle */}
              <Button
                onClick={toggleOnlineStatus}
                disabled={toggling}
                className={`flex items-center gap-2 ${
                  isOnline 
                    ? 'bg-green-600 hover:bg-green-700' 
                    : 'bg-gray-500 hover:bg-gray-600'
                }`}
              >
                <Power className="h-4 w-4" />
                {toggling ? 'Updating...' : isOnline ? '🟢 Online' : '⚫ Offline'}
              </Button>
              <Button
                onClick={handleLogout}
                variant="outline"
                className="flex items-center gap-2"
              >
                <LogOut className="h-4 w-4" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Offline Warning Banner */}
      {!isOnline && (
        <div className="bg-yellow-50 border-b border-yellow-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2">
            <div className="flex items-center justify-between">
              <p className="text-sm text-yellow-800">
                <strong>You are offline.</strong> Go online to see available rides and start earning.
              </p>
              <Button 
                onClick={toggleOnlineStatus}
                size="sm"
                className="bg-yellow-600 hover:bg-yellow-700 text-white"
                disabled={toggling}
              >
                Go Online Now
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex gap-8">
          {/* Sidebar */}
          <div className="w-64 flex-shrink-0">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <nav className="space-y-2">
                {menuItems.map((item) => {
                  const Icon = item.icon;
                  const isActive = location.pathname === item.path;
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                        isActive
                          ? 'bg-pink-50 text-pink-600 font-medium'
                          : 'text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      <Icon className="h-5 w-5" />
                      {item.label}
                    </Link>
                  );
                })}
              </nav>
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1">
            {children}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DriverLayout;